import { getAddress, type Address } from 'viem'

export type TokenMeta = {
  address: Address
  symbol: string
  name: string
  decimals: number
  /** Another token on the list reports the same symbol, so the picker shows the address. */
  symbolCollision?: boolean
}

/**
 * Native FLR, as the API spells it: the aggregator sentinel, never a contract.
 *
 * Mirrors `NATIVE_TOKEN_ADDRESS` in `packages/shared/src/chain.ts` — change one, change the
 * other. Nothing may read this address on chain; balances come from `eth_getBalance`, and a
 * native input needs no approval because the router is paid by the transaction's value.
 */
export const NATIVE_TOKEN_ADDRESS = getAddress('0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE')

export const NATIVE_TOKEN: TokenMeta = {
  address: NATIVE_TOKEN_ADDRESS,
  symbol: 'FLR',
  name: 'Flare (native)',
  decimals: 18,
}

export const WFLR_ADDRESS = getAddress('0x1D80c49BbBCd1C0911346656B529DF9E5c2F783d')

export function isNative(token: TokenMeta | Address): boolean {
  return (typeof token === 'string' ? token : token.address) === NATIVE_TOKEN_ADDRESS
}

/**
 * FLR against WFLR is a wrap, not a trade: 1:1 through WNat, nothing to route, and the API
 * refuses to quote it (`SAME_TOKEN`). The swap card does it with `deposit` / `withdraw`.
 */
export function wrapDirection(tokenIn: TokenMeta, tokenOut: TokenMeta): 'wrap' | 'unwrap' | null {
  if (isNative(tokenIn) && tokenOut.address === WFLR_ADDRESS) return 'wrap'
  if (tokenIn.address === WFLR_ADDRESS && isNative(tokenOut)) return 'unwrap'
  return null
}

/**
 * The tokens offered until `GET /tokens` answers, and in its place when it cannot.
 *
 * This is the base-token table from CLAUDE.md, which is the routing engine's
 * intermediate-hop backbone — not a claim about what is routable. Once the API's list has
 * loaded it replaces this one entirely (see `useTokenList`); the API answers
 * `UNKNOWN_TOKEN` for anything it will not route, which is the only authority on the
 * question.
 *
 * Addresses, never symbols. Two tokens on this chain report the symbol `WFLR` and three
 * report `FXRP`, so a symbol lookup would pick one of them by luck.
 *
 * Native FLR leads the list: it is the gas token, so it is what most wallets here hold.
 */
export const DEFAULT_TOKENS: TokenMeta[] = [
  NATIVE_TOKEN,
  {
    address: WFLR_ADDRESS,
    symbol: 'WFLR',
    name: 'Wrapped Flare',
    decimals: 18,
  },
  {
    address: getAddress('0xe7cd86e13AC4309349F30B3435a9d337750fC82D'),
    symbol: 'USD₮0',
    name: 'Tether USD₮0',
    decimals: 6,
  },
  {
    address: getAddress('0xFbDa5F676cB37624f28265A144A48B0d6e87d3b6'),
    symbol: 'USDC.e',
    name: 'Bridged USDC',
    decimals: 6,
  },
  {
    address: getAddress('0x1502FA4be69d526124D453619276FacCab275d3D'),
    symbol: 'WETH',
    name: 'Wrapped Ether',
    decimals: 18,
  },
  {
    address: getAddress('0xAd552A648C74D49E10027AB8a618A3ad4901c5bE'),
    symbol: 'FXRP',
    name: 'FXRP',
    decimals: 6,
  },
]

export function findToken(list: TokenMeta[], address: Address | null): TokenMeta | null {
  if (!address) return null
  const wanted = getAddress(address)
  return list.find((token) => token.address === wanted) ?? null
}
